import React, {ReactNode, useCallback, useEffect, useMemo, useState} from "react";
import {Button, Col, Input, Row, Tooltip} from "antd";
import { CloseOutlined, EditOutlined, SaveOutlined } from "@ant-design/icons";
import ee from "../../../services/EventEmitter";
import { CHANGE_TABLE_CELL_EVENT } from "../../../constants/events";

interface Props {
  string: string;
  entityKey: string;
  entityId: unknown;
  eventKey?: string;
  format?: Partial<{
    formatter: (value: string) => string;
    reverter: (value: string) => string;
  }>;
  validation?: Partial<{
    validator: (value: string) => boolean;
    message: string;
  }>;
}

const SaveWrapper = ({ children, valid, message }: { children: ReactNode, valid: boolean, message: string }) => {
  if(!valid) {
    return (
      <Tooltip
        title={message}
      >
        <div>
          {children}
        </div>
      </Tooltip>
    );
  } else {
    return <>
      {children}
    </>;
  }
};

const TextArea: React.FC<Props> = ({
  string,
  entityKey,
  entityId,
  eventKey = CHANGE_TABLE_CELL_EVENT,
  format,
  validation,
}) => {
  const [value, setValue] = useState<string>(string);
  const [isEditable, setIsEditable] = useState<boolean>(false);

  const revertValue = useCallback(
    (nextValue: string) => {
      return (format?.reverter ? format.reverter(nextValue) : nextValue);
    },
    [format]
  );

  const formatValue = useCallback(
    (nextValue: string) => (format?.formatter ? format.formatter(revertValue(nextValue)) : nextValue),
    [format, revertValue]
  );

  const isValueValid = useMemo(() => {
    return validation?.validator ? validation.validator(revertValue(value)) : true
  }, [validation, value]);

  const onResetHandler = useCallback(() => {
    setIsEditable(false);
    setValue(formatValue(string));
  }, [string]);

  const onSaveHandler = useCallback(() => {
    const rawValue: string = revertValue(value);

    ee.emit(eventKey, {
      entityId,
      entityKey,
      value: rawValue,
    });

    setValue(formatValue(value));
    setIsEditable(false);
  }, [entityId, entityKey, eventKey, formatValue, revertValue, value]);

  useEffect(() => {
    setValue(formatValue(string));
  }, [string]);

  if (isEditable) {
    return (
      <Row align="middle" justify="space-between" className="table-textarea">
        <Col className="table-textarea__control">
          <Input
            value={formatValue(value)}
            onChange={(event) => setValue(event.target.value)}
          />
        </Col>
        <Col className="table-textarea__actions">
          <SaveWrapper message={validation?.message || "Invalid format"} valid={isValueValid}>
            <Button
              type="link"
              onClick={onSaveHandler}
              disabled={!isValueValid}
              className="single-button"
            >
              <SaveOutlined />
            </Button>
          </SaveWrapper>
          <Button
            type="link"
            onClick={onResetHandler}
            className="single-button"
          >
            <CloseOutlined />
          </Button>
        </Col>
      </Row>
    );
  }

  return (
    <div className="textarea-preview">
      {string}
      <Button
        type="link"
        onClick={() => setIsEditable(!isEditable)}
        className="textarea-preview__actions"
      >
        <EditOutlined />
      </Button>
    </div>
  );
};

export default TextArea;
